import EqualizerIcon from "@mui/icons-material/Equalizer";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import { Chip, MenuItem, Select, SelectChangeEvent, Stack } from "@mui/material";
import {
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
  GridRowParams,
  GridRowsProp,
} from "@mui/x-data-grid";
import { ChangeEvent, FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { debounce } from "throttle-debounce";
import { shareService } from "../../Core/Api/services";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { DbShareMediaInfos } from "../../Core/Api/Types/db-share-media-infos";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { OrderStatus } from "../../Core/Api/Types/order-status";
import { ShareMediaType } from "../../Core/Api/Types/share-media-type";
import { useToggle } from "../../Utils/Hooks/useToggle";
import { ModalContext } from "../Modals";
import ShareDetailsPanel from "../Panels/ShareDetailsPanel/share-panel";
import TableCellChip from "../Table/TableCellChip/table-cell-chip";
import TableCellDateTime from "../Table/TableCellDateTime/table-cell-date-time";
import TableCellExpiresIn from "../Table/TableCellExpiresIn/table-cell-expires-in";
import TableCellShareClips from "../Table/TableCellShareClips/table-cell-share-clips";
import TableCellShareStatus from "../Table/TableCellShareStatus/table-cell-share-status";
import TableSkeleton from "../Table/TableSkeleton";
import ModalConfirmAction, { ModalConfirmActionElementProps } from "../Modals/ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import ModalShareUpdate, { ModalShareUpdateElementProps } from "../Modals/ModalShareUpdate/modal-share-update";
import { produce } from "immer";
import ModalShareMetrics, { ModalShareMetricsElementProps } from "../Modals/ModalShareMetrics/modal-share-metrics";
import TableCellTypography from "../Table/TableCellTypography/table-cell-typography";
import DataTable, { CellSize } from "../Table/table";
import { useTranslation } from "react-i18next";
import TableUuids from "../Table/table-uuids";
import { GetMetadataByTagName } from "../../Core/Utils/asset-object-utils";

export interface AdminAssetShareFilters {
  type: ShareMediaType[];
  object: ObjectLayer[];
  status: string[];
}

const AssetShareDatatable: FC<{ asset: DbAssetObjectInfos | undefined }> = (props) => {
  const { t } = useTranslation();
  const { asset } = props;

  const enqueueSnackbar = useCustomSnackbar();

  const [rows, setRows] = useState<DbShareMediaInfos[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [tableFilterModel, setTableFilterModel] = useState<GridFilterModel>({ items: [] });
  const [filtersData, setFiltersData] = useState<AdminAssetShareFilters>({ type: [], object: [], status: [] });
  const [typeFilter, setTypeFilter] = useState("");
  const [objectFilter, setObjectFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedShare, setSelectedShare] = useState<DbShareMediaInfos>();
  const { active: shareDetailsPanelOpen, toggle: toggleShareDetailsPanelOpen } = useToggle(false);

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const updateFilterModel = (newFilterItems: GridFilterItem[]) => {
    setTableFilterModel({
      items: newFilterItems,
      quickFilterLogicOperator: GridLogicOperator.And,
      logicOperator: GridLogicOperator.Or,
    });
  };

  const updateClipsImportedStatus = (shareId, ClipIds: number[], linkedAssetId: number) => {
    let rowIndex = rows.findIndex((x) => x.id == shareId);
    let row = rows[rowIndex];
    row?.clips?.forEach((x) => {
      if (ClipIds.includes(x.id)) x.importedOnAssetId = linkedAssetId;
    });

    setRows(rows.map((x, index) => (index === rowIndex ? row : x)));
  };

  let _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];
  const columns: GridColDef[] = [
    {
      field: "type",
      headerName: t("common:type"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      valueGetter: (data: any, row: any) => (data === "StreamingTagSecure" ? "Secured streaming" : "Basic"),
      renderCell: (data: Row) => <TableCellTypography fontWeight={500} data={data.value} />,
    },
    {
      field: "objectLayer",
      headerName: t("common:object"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      hideable: true,
      renderCell: (data: Row) => (
        <Chip label={data.value} key={data.value} size="small" color={"default"} sx={{ mr: 0.5 }} />
      ),
    },
    {
      field: "createdByUserName",
      headerName: t("common:creator"),
      minWidth: CellSize.large,
      flex: CellSize.large,
      renderCell: (data: Row) => <TableCellTypography data={data.value} />,
    },
    {
      field: "creationInfos",
      headerName: t("common:createdAtLowerCase"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (data: Row) => <TableCellDateTime data={data} />,
    },
    {
      field: "daysUntilExpire",
      headerName: t("table:headerName.expiresIn"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (data: Row) => (data.value >= 0 ? <TableCellExpiresIn daysNumber={data.value} /> : ""),
    },
    {
      field: "isActive",
      headerName: t("common:status"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data: Row) => {
        let transcoding =
          data?.row?.linkedOrderItemStatus === OrderStatus.READY ||
          data?.row?.linkedOrderItemStatus === OrderStatus.QUEUED ||
          data?.row?.linkedOrderItemStatus === OrderStatus.RUNNING;

        return (
          <TableCellShareStatus
            data={{
              value: data.value,
              transcoding: transcoding,
              nbViews: data?.row?.viewInfos?.length ?? 0,
              nbTotalViews: data?.row?.viewsMax,
            }}
          />
        );
      },
    },
    {
      field: "clips",
      headerName: t("common:clips"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (data: Row) => (
        <TableCellShareClips
          clips={data.value}
          shareId={data?.row?.id}
          clipsCallback={(clipIds, linkedAssetId) => {
            updateClipsImportedStatus(data?.row?.id, clipIds, linkedAssetId);
          }}
        />
      ),
    },
    {
      field: "recipientEmails",
      headerName: t("table:headerName.sharedTo"),
      minWidth: CellSize.xlarge,
      flex: CellSize.xlarge,
      renderCell: (data: Row) => <TableCellChip data={data} />,
    },
  ];

  useEffect(() => {
    if (asset) {
      shareService.GetAllSharesByObjectId(asset.id).then(function (response) {
        if (response.shares !== null) {
          let types: ShareMediaType[] = [];
          response.shares.map((share) => {
            if (share.type !== null && share.type !== undefined && !types.includes(share.type)) types.push(share.type);
          });

          let objects: ObjectLayer[] = [];
          response.shares.map((share) => {
            if (share.objectLayer !== null && share.objectLayer !== undefined && !objects.includes(share.objectLayer))
              objects.push(share.objectLayer);
          });

          let status: string[] = [];
          if (
            response.shares.some(
              (x) => x.isActive !== undefined && x.isActive !== null && x.objectLayer && x.isActive === true
            )
          )
            status.push("Active");
          if (
            response.shares.some(
              (x) => x.isActive !== undefined && x.isActive !== null && x.objectLayer && x.isActive === false
            )
          )
            status.push("Expired");

          setRows(response.shares);
          setFiltersData({ type: types, object: objects, status: status });
          setIsLoading(false);
        }
      });
    }
  }, [asset]);

  const handleChangeTypeSelect = (event: SelectChangeEvent) => {
    const typeFilterValue = event.target.value as string;
    // always remove filter
    let newFilterItems: GridFilterItem[] = tableFilterModel.items.filter((x) => x.field !== "type");
    // if providerName selected :
    if (typeFilterValue !== "") {
      // add filter on providerName
      newFilterItems.push({
        id: 0,
        field: "type",
        operator: "equals",
        value: typeFilterValue,
      });
    }
    // set states
    updateFilterModel(newFilterItems);
    setTypeFilter(typeFilterValue);
  };

  const handleChangeObjectSelect = (event: SelectChangeEvent) => {
    const objectFilterValue = event.target.value as string;
    // always remove filter
    let newFilterItems: GridFilterItem[] = tableFilterModel.items.filter((x) => x.field !== "objectLayer");
    // if providerName selected :
    if (objectFilterValue !== "") {
      // add filter on providerName
      newFilterItems.push({
        id: 0,
        field: "objectLayer",
        operator: "equals",
        value: objectFilterValue,
      });
    }
    // set states
    updateFilterModel(newFilterItems);
    setObjectFilter(objectFilterValue);
  };

  const handleChangeStatusSelect = (event: SelectChangeEvent) => {
    const statusFilterValue = event.target.value as string;
    // always remove filter
    let newFilterItems: GridFilterItem[] = tableFilterModel.items.filter((x) => x.field !== "isActive");
    // if providerName selected :
    if (statusFilterValue !== "") {
      // add filter on providerName
      newFilterItems.push({
        id: 0,
        field: "isActive",
        operator: "equals",
        value: statusFilterValue === "Active" ? "true" : "false",
      });
    }
    // set states
    updateFilterModel(newFilterItems);
    setStatusFilter(statusFilterValue);
  };

  // search input management
  const updateSearchValue = useCallback(
    (newSearchValue: string) => {
      let newFilterItems: GridFilterItem[] = tableFilterModel.items.filter((x) => x.field !== "customer");
      newFilterItems.push({
        id: 8,
        field: "customer",
        operator: "contains",
        value: newSearchValue,
      });
      updateFilterModel(newFilterItems);
    },
    [tableFilterModel]
  );

  const debouncedUpdateSearchValue = useMemo(() => debounce(500, updateSearchValue), [updateSearchValue]);

  const handleSearchValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newSearchValue = event.target.value;
      setSearchValue(newSearchValue);
      debouncedUpdateSearchValue(newSearchValue);
    },
    [debouncedUpdateSearchValue]
  );

  const openPanelForRowDetails = (e: GridRowParams) => {
    setSelectedShare(e.row as DbShareMediaInfos);
    toggleShareDetailsPanelOpen();
  };

  const onSubmitEdit = (share: DbShareMediaInfos) => {
    setRows(
      produce((draft) => {
        let index = draft.findIndex((x) => x.id === share.id);

        if (index !== -1) draft[index] = share;
      })
    );
  };

  const handleEditShare = (share) => {
    if (shareDetailsPanelOpen) toggleShareDetailsPanelOpen();
    if (!openModal) return;

    openModal<ModalShareUpdateElementProps>(ModalShareUpdate, {
      share: share,
      onUpdated: onSubmitEdit,
    });
  };

  const handleDeleteShare = (share: DbShareMediaInfos) => {
    if (shareDetailsPanelOpen) toggleShareDetailsPanelOpen();

    setSelectedShare(share);
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      commentLabel: t("modal:comment.confirmShareDeletion"),
      title: t("modal:title.confirmShareDeletion"),
      onConfirm: () => handleDeleteShareConfirmed(share),
    });
  };

  const handleDeleteShareConfirmed = (share: DbShareMediaInfos) => {
    shareService.DeleteShareById(share.id).then(() => {
      enqueueSnackbar(t("alert:success.shareDeleted"), "success");
      setRows((current) =>
        current.filter((c) => {
          return c.id !== share.id;
        })
      );

      if (share.linkedShareIds)
        setRows((current) =>
          current.filter((c) => {
            return !share.linkedShareIds?.includes(c.id);
          })
        );
    });
  };

  const handleMetricsShare = (share) => {
    if (shareDetailsPanelOpen) toggleShareDetailsPanelOpen();
    if (!openModal) return;

    openModal<ModalShareMetricsElementProps>(ModalShareMetrics, {
      share: share,
    });
  };

  const additionalFilters = (
    <>
      <Select
        displayEmpty
        startAdornment={<ViewInArOutlinedIcon />}
        IconComponent={ExpandMoreRoundedIcon}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={typeFilter}
        onChange={handleChangeTypeSelect}
      >
        <MenuItem value="">{t("common:type")}</MenuItem>
        <MenuItem value={ShareMediaType.StreamingBasic} key="type-basic">
          {t("common:basic")}
        </MenuItem>
        <MenuItem value={ShareMediaType.StreamingTagSecure} key="type-tag">
          {t("menu:item.securedStreaming")}
        </MenuItem>
      </Select>
      <Select
        displayEmpty
        startAdornment={<LightbulbOutlinedIcon />}
        IconComponent={ExpandMoreRoundedIcon}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={objectFilter}
        onChange={handleChangeObjectSelect}
      >
        <MenuItem value="">{t("common:object")}</MenuItem>
        {filtersData.object.length > 0 &&
          filtersData.object.map((object) => (
            <MenuItem key={object} value={object}>
              {object}
            </MenuItem>
          ))}
      </Select>
      <Select
        displayEmpty
        startAdornment={<EqualizerIcon />}
        IconComponent={ExpandMoreRoundedIcon}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={statusFilter}
        onChange={handleChangeStatusSelect}
      >
        <MenuItem value="">{t("common:status")}</MenuItem>
        {filtersData.status.length > 0 &&
          filtersData.status.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
      </Select>
    </>
  );

  return (
    <Stack minHeight={"calc(100vh - 129px)"}>
      {!asset ? (
        <TableSkeleton />
      ) : (
        <>
          <DataTable
            loading={isLoading}
            tableKey={TableUuids.ASSET_SINGLE_SHARE_TABLE}
            rows={rows}
            columns={columns}
            checkboxSelection={false}
            disableRowSelectionOnClick
            pagination={true}
            onRowClick={openPanelForRowDetails}
            initialState={{
              sorting: { sortModel: [{ field: "start", sort: "desc" }] },
              columns: {
                columnVisibilityModel: {
                  objectLayer: asset.layer === ObjectLayer.Title,
                },
              },
            }}
            filterModel={tableFilterModel}
            additionalFilters={additionalFilters}
          />
          <ShareDetailsPanel
            share={selectedShare}
            open={shareDetailsPanelOpen}
            onClose={toggleShareDetailsPanelOpen}
            openEditModal={() => handleEditShare(selectedShare)}
            openMetricsModal={() => handleMetricsShare(selectedShare)}
            deleteShare={() => handleDeleteShareConfirmed(selectedShare!)}
          />
        </>
      )}
    </Stack>
  );
};

export default AssetShareDatatable;
